import React, { useEffect, useState } from 'react';
import {
	MsalProvider,
	AuthenticatedTemplate,
	UnauthenticatedTemplate,
	useMsal,
} from '@azure/msal-react';
import { InteractionStatus, EventType, EventMessage, AuthenticationResult, PublicClientApplication } from '@azure/msal-browser';
import { isAuthMocked } from '../config/config';
import { useConfigContext } from '../config/configContext';
import { createMsalConfig, loginRequest } from '../msalConfig';
import AuthErrorPage from '../components/AuthErrorPage';

// Cooldown to prevent an infinite reload loop when the authority is genuinely broken.
const ENDPOINTS_RELOAD_KEY = 'auth_endpoints_reload';
const ENDPOINTS_RELOAD_COOLDOWN_MS = 30_000;

const MsalAuthenticator: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { instance, inProgress } = useMsal();
	const { config } = useConfigContext();
	const [authError, setAuthError] = useState<string | null>(null);

	useEffect(() => {
		const callbackId = instance.addEventCallback((event: EventMessage) => {
			// Stale MSAL cache from a previous authority (e.g. sts.windows.net tokens after
			// switching to login.microsoftonline.com/organizations) causes acquireTokenSilent
			// and loginRedirect to fail. Clearing sessionStorage via clearCache() removes the
			// stale tokens, but the in-memory MSAL instance still holds the old authority state.
			// A full page reload creates a fresh instance that can resolve endpoints correctly.
			// A sessionStorage timestamp prevents an infinite reload loop when the authority is
			// genuinely misconfigured.
			const handleEndpointsResolutionError = () => {
				const lastReload = sessionStorage.getItem(ENDPOINTS_RELOAD_KEY);
				const now = Date.now();
				if (lastReload && now - Number(lastReload) < ENDPOINTS_RELOAD_COOLDOWN_MS) {
					setAuthError('Error during the authentication process. Please try again later.');
					return;
				}
				sessionStorage.setItem(ENDPOINTS_RELOAD_KEY, String(now));
				instance.clearCache()
					.then(() => window.location.reload())
					.catch((e) => console.error('Cache clear error:', e));
			};

			if (event.eventType === EventType.LOGIN_FAILURE) {
				if ((event.error as { errorCode?: string })?.errorCode === 'endpoints_resolution_error') {
					handleEndpointsResolutionError();
					return;
				}
				setAuthError('Login failed. Please try again.');
			}
			if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
				if ((event.error as { errorCode?: string })?.errorCode === 'endpoints_resolution_error') {
					handleEndpointsResolutionError();
					return;
				}
				setAuthError('Failed to acquire token. Please try again.');
			}
			if (event.eventType === EventType.LOGIN_SUCCESS) {
				const result = event.payload as AuthenticationResult;
				if (result && result.account) {
					instance.setActiveAccount(result.account);
				}
				sessionStorage.removeItem(ENDPOINTS_RELOAD_KEY);
			}
		});

		return () => {
			if (callbackId) {
				instance.removeEventCallback(callbackId);
			}
		};
	}, [instance]);

	useEffect(() => {
		// Once an auth error is shown do not attempt another redirect — the user must
		// manually retry (e.g. by refreshing the page).
		if (authError) return;

		if (inProgress === InteractionStatus.None) {
			const accounts = instance.getAllAccounts();
			if (accounts.length === 0) {
				instance.loginRedirect(loginRequest(config.msal.scopes)).catch((error) => {
					console.error('Login redirect error:', error);
					// endpoints_resolution_error is handled by the LOGIN_FAILURE event above.
					if ((error as { errorCode?: string })?.errorCode !== 'endpoints_resolution_error') {
						setAuthError('Failed to initiate login. Please try again.');
					}
				});
			} else if (!instance.getActiveAccount()) {
				instance.setActiveAccount(accounts[0]);
			}
		}
	}, [instance, inProgress, config.msal.scopes, authError]);

	if (authError) {
		return <AuthErrorPage />;
	}

	return (
		<>
			<AuthenticatedTemplate>{children}</AuthenticatedTemplate>
			<UnauthenticatedTemplate>
				{inProgress === InteractionStatus.None ? <AuthErrorPage /> : <div />}
			</UnauthenticatedTemplate>
		</>
	);
};

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { config } = useConfigContext();
	const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);
	const [initError, setInitError] = useState(false);

	useEffect(() => {
		if (isAuthMocked) return;

		const initializeMsal = async () => {
			try {
				const msalConfig = createMsalConfig(config.msal);
				const instance = new PublicClientApplication(msalConfig);
				
				// Initialize MSAL instance (required in v3.x)
				await instance.initialize();
				
				// Handle redirect response
				await instance.handleRedirectPromise();
				
				setMsalInstance(instance);
			} catch (error) {
				console.error('MSAL initialization error:', error);
				setInitError(true);
			}
		};

		initializeMsal();
	}, [config.msal]);

	if (isAuthMocked) {
		return <>{children}</>;
	}

	if (initError) {
		return <AuthErrorPage />;
	}

	if (!msalInstance) {
		return <div />;
	}

	return (
		<MsalProvider instance={msalInstance}>
			<MsalAuthenticator>{children}</MsalAuthenticator>
		</MsalProvider>
	);
};

export default AuthProvider;
